import { Box, Button, HStack, IconButton, Menu, Portal, Spinner, Text, VStack } from '@chakra-ui/react';
import { useState } from 'react';

import { ADVISOR_SIDE_PANEL_PORTAL_POPOVER_Z_INDEX } from './advisor-side-panel';
import { KintsugiAiIconTrigger } from './kintsugi-ai-icon-trigger';

export type AdvisorHistoryThreadRow = {
  id: string;
  title: string | null;
  /** Agent lane for ``GET``/``DELETE`` ``/v2/chat/threads/*`` (from list payload ``use_case``). */
  useCase?: string;
  /** Pre-formatted last-activity label (e.g. from ``formatChatHistoryUpdatedAt``). */
  updatedAtLabel?: string;
};

/**
 * How a row's destructive action is surfaced.
 * ``always-visible-trash`` (default) keeps the legacy drawer rendering: a trash icon is shown on every row.
 * ``hover-kebab-menu`` matches the Figma Chat History design (file ``Dashboard``, frame ``Kintsugi Advisor - Hover and Kebab Menu``
 * / ``... Kebab Menu Click``, nodes ``10968:24018`` / ``10968:24132``): the timestamp is shown by default; on row
 * hover (or keyboard focus within the row) it is replaced by a kebab trigger that opens a small menu containing a red
 * "Delete" action.
 */
export type AdvisorThreadHistoryRowActionsMode = 'always-visible-trash' | 'hover-kebab-menu';

export type AdvisorThreadHistoryListProps = {
  threads: AdvisorHistoryThreadRow[];
  loading: boolean;
  errorText?: string | null;
  onSelectThread: (row: AdvisorHistoryThreadRow) => void;
  /**
 * When set, shows a delete control per row. Invoked when the user activates delete; parent runs the API and error handling.
 */
  onDeleteThread?: (row: AdvisorHistoryThreadRow) => void | Promise<void>;
    deletingThreadId?: string | null;
    emptyLabel?: string;
  /** @see {@link AdvisorThreadHistoryRowActionsMode}. Defaults to ``always-visible-trash``. */
  rowActionsMode?: AdvisorThreadHistoryRowActionsMode;
};

const trashIcon = (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M3 6h18" strokeLinecap="round" />
    <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" />
    <path d="M10 11v6M14 11v6" strokeLinecap="round" />
  </svg>
);

const KebabIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" aria-hidden focusable={false}>
    <circle cx="12" cy="5" r="1.6" />
    <circle cx="12" cy="12" r="1.6" />
    <circle cx="12" cy="19" r="1.6" />
  </svg>
);

function threadLabel(row: AdvisorHistoryThreadRow): string {
  const t = row.title?.trim();
  if (t) return t;
  return `Conversation ${row.id.slice(0, 8)}…`;
}

type HoverKebabRowProps = {
  row: AdvisorHistoryThreadRow;
  busy: boolean;
  isLast: boolean;
  onDeleteThread?: (row: AdvisorHistoryThreadRow) => void | Promise<void>;
  onSelectThread: (row: AdvisorHistoryThreadRow) => void;
};

/**
 * Figma “hover to show ⋮” row. Uses pointer + menu state instead of Chakra ``_groupHover`` — group-hover
 * styles are not reliable in all Chakra v3 + portal setups; portaled menu content also leaves the row
 * for pointer-events, so visibility must be ``row hover OR menu open``.
 */
function HoverKebabThreadRow({ row, busy, isLast, onDeleteThread, onSelectThread }: HoverKebabRowProps) {
  const [rowPointerInside, setRowPointerInside] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const showKebab = rowPointerInside || menuOpen;
  const label = threadLabel(row);

  return (
    <Box
      role="listitem"
      aria-busy={busy || undefined}
      borderBottomWidth={isLast ? 0 : '1px'}
      borderColor="gray.200"
      transition="background-color 0.12s"
      bg={rowPointerInside || menuOpen ? 'gray.50' : 'transparent'}
      onPointerEnter={() => setRowPointerInside(true)}
      onPointerLeave={() => setRowPointerInside(false)}
      onFocusCapture={() => {
        setRowPointerInside(true);
      }}
      onBlurCapture={e => {
        const next = e.relatedTarget as Node | null;
        if (next && e.currentTarget.contains(next)) {
          return;
        }
        setRowPointerInside(false);
      }}
      opacity={busy ? 0.5 : 1}
    >
      <HStack align="center" gap={2} minH="55px" pl={2} pr={1}>
        <Button
          type="button"
          variant="ghost"
          flex="1"
          minW={0}
          justifyContent="flex-start"
          h="auto"
          py={2}
          px={1}
          fontWeight="normal"
          _hover={{ bg: 'transparent' }}
          onClick={() => onSelectThread(row)}
          disabled={busy}
          aria-label={`Open conversation: ${label}`}
        >
          <Text
            fontSize="sm"
            color="gray.800"
            textAlign="start"
            truncate
            css={{ maxWidth: '100%' }}
          >
            {label}
          </Text>
        </Button>
        <Box
          w="64px"
          flexShrink={0}
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          position="relative"
        >
          <Text
            fontSize="xs"
            color="gray.600"
            textAlign="right"
            lineHeight="18px"
            transition="opacity 0.12s"
            opacity={onDeleteThread && showKebab ? 0 : 1}
            pointerEvents={onDeleteThread && showKebab ? 'none' : 'auto'}
            css={onDeleteThread ? { '@media (hover: none)': { display: 'none' } } : undefined}
            aria-hidden={onDeleteThread && showKebab ? true : undefined}
          >
            {row.updatedAtLabel?.trim() ? row.updatedAtLabel : '\u00a0'}
          </Text>
          {onDeleteThread ? (
            <Box
              position="absolute"
              right={0}
              top="50%"
              transform="translateY(-50%)"
              opacity={showKebab ? 1 : 0}
              transition="opacity 0.12s"
              pointerEvents="auto"
              css={{ '@media (hover: none)': { opacity: 1 } }}
            >
              <Menu.Root
                positioning={{ placement: 'bottom-end' }}
                onOpenChange={({ open }) => setMenuOpen(open)}
                onSelect={({ value }) => {
                  if (value === 'delete' && !busy) {
                    void onDeleteThread(row);
                  }
                }}
              >
                <Menu.Trigger asChild>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label={busy ? 'Deleting conversation' : 'Conversation options'}
                    disabled={busy}
                    color="gray.600"
                    _hover={{ bg: 'gray.100' }}
                  >
                    <KebabIcon />
                  </IconButton>
                </Menu.Trigger>
                <Portal>
                  <Menu.Positioner zIndex={ADVISOR_SIDE_PANEL_PORTAL_POPOVER_Z_INDEX}>
                    <Menu.Content minW="120px" py={1}>
                      <Menu.Item value="delete" color="red.600" _hover={{ bg: 'red.50', color: 'red.600' }}>
                        Delete
                      </Menu.Item>
                    </Menu.Content>
                  </Menu.Positioner>
                </Portal>
              </Menu.Root>
            </Box>
          ) : null}
        </Box>
      </HStack>
    </Box>
  );
}

export function AdvisorThreadHistoryList({
  threads,
  loading,
  errorText,
  onSelectThread,
  onDeleteThread,
  deletingThreadId = null,
  emptyLabel = 'No past conversations yet.',
  rowActionsMode = 'always-visible-trash',
}: AdvisorThreadHistoryListProps) {
  if (loading) {
    return (
      <VStack py={10} gap={3}>
        <Spinner size="md" color="teal.500" />
        <Text fontSize="sm" color="gray.600">
          Loading…
        </Text>
      </VStack>
    );
  }

  if (errorText?.trim()) {
    return (
      <Box py={6} px={1}>
        <Text fontSize="sm" color="red.600">
          {errorText.trim()}
        </Text>
      </Box>
    );
  }

  if (!threads.length) {
    return (
      <Box py={8} px={1}>
        <Text fontSize="sm" color="gray.600">
          {emptyLabel}
        </Text>
      </Box>
    );
  }

  if (rowActionsMode === 'hover-kebab-menu') {
    return (
      <VStack align="stretch" gap={0} py={0} role="list">
        {threads.map((row, index) => (
          <HoverKebabThreadRow
            key={row.id}
            row={row}
            busy={deletingThreadId === row.id}
            isLast={index === threads.length - 1}
            onDeleteThread={onDeleteThread}
            onSelectThread={onSelectThread}
          />
        ))}
      </VStack>
    );
  }

  return (
    <VStack align="stretch" gap={0} py={0}>
      {threads.map(row => {
        const busy = deletingThreadId === row.id;
        return (
          <Box
            key={row.id}
            role="group"
            borderBottomWidth="1px"
            borderColor="gray.200"
            py={2}
            px={2}
            _last={{ borderBottomWidth: 0 }}
          >
            <HStack align="flex-start" gap={2} minH="44px">
              <Button
                type="button"
                variant="ghost"
                flex="1"
                minW={0}
                justifyContent="flex-start"
                h="auto"
                py={0}
                px={1}
                fontWeight="normal"
                onClick={() => onSelectThread(row)}
              >
                <Text
                  fontSize="sm"
                  color="gray.800"
                  textAlign="start"
                  css={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical' as const,
                    overflow: 'hidden',
                  }}
                >
                  {threadLabel(row)}
                </Text>
              </Button>
              <Text
                fontSize="xs"
                color="gray.600"
                flexShrink={0}
                w="76px"
                textAlign="right"
                lineHeight="18px"
                pt={0.5}
              >
                {row.updatedAtLabel?.trim() ? row.updatedAtLabel : '\u00a0'}
              </Text>
              {onDeleteThread ? (
                <Box flexShrink={0} opacity={busy ? 0.45 : 1} pointerEvents={busy ? 'none' : 'auto'}>
                  <KintsugiAiIconTrigger
                    inactive
                    ariaLabel={busy ? 'Deleting conversation' : 'Delete conversation'}
                    icon={trashIcon}
                    onClick={() => {
                      if (busy) return;
                      void onDeleteThread(row);
                    }}
                  />
                </Box>
              ) : null}
            </HStack>
          </Box>
        );
      })}
    </VStack>
  );
}
